import apiClient from '../utils/apiClient';

/**
 * Per-admin push preferences + a diagnostics probe.
 *
 * Separate from `notificationApi` on purpose: that file is the notification
 * *list* CRUD, this one is about the delivery channel itself.
 */

export interface PushCategoryMeta {
  id: string;
  label: string;
  description: string;
  importance: 'high' | 'default' | 'low';
  /** false → the user is not allowed to silence it (finance, security). */
  mutable: boolean;
}

export interface PushDevice {
  platform: string;
  deviceModel?: string;
  appVersion?: string;
  lastSeenAt?: string;
}

export interface QuietHours {
  enabled: boolean;
  startHour: number;
  endHour: number;
}

export interface NotificationPreferences {
  pushEnabled: boolean;
  mutedCategories: string[];
  quietHours: QuietHours;
  categories: PushCategoryMeta[];
  devices: PushDevice[];
}

export interface PushDiagnostics {
  /** Server-side master switch (FIREBASE_ENABLED + credentials). */
  firebaseEnabled: boolean;
  firebaseProjectId: string | null;
  /** Devices registered for THIS account. */
  devices: number;
  devicesByPlatform: Record<string, number>;
  legacyTokens: number;
  preferences: {
    pushEnabled?: boolean;
    mutedCategories?: string[];
    quietHours?: { enabled?: boolean; startHour?: number; endHour?: number };
  } | null;
  sample: {
    type: string;
    context: string;
    category: string;
    webPath: string | null;
    appRoute: string | null;
  };
}

interface Result<T> {
  success: boolean;
  data?: T;
  message?: string;
}

const fail = (error: any, fallback: string): Result<never> => ({
  success: false,
  message: error?.response?.data?.message || fallback,
});

export const pushSettingsApi = {
  async getPreferences(): Promise<Result<NotificationPreferences>> {
    try {
      const res = await apiClient.get('/notifications/preferences');
      return { success: true, data: res.data?.data };
    } catch (error) {
      return fail(error, 'خطا در دریافت تنظیمات اعلان‌ها');
    }
  },

  async updatePreferences(payload: {
    pushEnabled?: boolean;
    mutedCategories?: string[];
    quietHours?: QuietHours;
  }): Promise<Result<NotificationPreferences>> {
    try {
      const res = await apiClient.put('/notifications/preferences', payload);
      return { success: true, data: res.data?.data, message: res.data?.message };
    } catch (error) {
      return fail(error, 'خطا در ذخیرهٔ تنظیمات اعلان‌ها');
    }
  },

  /** Answers "why is push not arriving?" without needing server logs. */
  async getDiagnostics(type = 'system', context = 'admin_panel'): Promise<Result<PushDiagnostics>> {
    try {
      const res = await apiClient.get('/notifications/push-diagnostics', {
        params: { type, context },
      });
      return { success: true, data: res.data?.data };
    } catch (error) {
      return fail(error, 'خطا در بررسی وضعیت پوش');
    }
  },

  /** Sends a real notification (in-app + push) to the logged-in admin. */
  async sendTestToSelf(
    title = 'تست پوش',
    message = 'اگر این پیام را روی دستگاه دیدید، پوش‌نوتیفیکیشن کار می‌کند.',
  ): Promise<Result<null>> {
    try {
      const res = await apiClient.post('/notifications/test', { title, message });
      return { success: true, message: res.data?.message };
    } catch (error) {
      return fail(error, 'خطا در ارسال اعلان تستی');
    }
  },

  async unregisterDevice(token: string): Promise<Result<null>> {
    try {
      await apiClient.delete('/notifications/device-token', { data: { token } });
      return { success: true };
    } catch (error) {
      return fail(error, 'خطا در حذف دستگاه');
    }
  },
};

// ── مدیریت پوشِ تک‌کاربر (پشتیبانی) — /api/admin/push/* ─────────────────────

export interface PushUserSearchResult {
  _id: string;
  name: string;
  phoneNumber?: string;
  email?: string;
}

export interface UserPushState {
  user: {
    _id: string;
    name: string;
    phoneNumber?: string;
    email?: string;
    isActive: boolean;
    isBanned: boolean;
  };
  preferences: {
    pushEnabled: boolean;
    mutedCategories: string[];
    quietHours: QuietHours;
    /** آیا کاربر تنظیمات سفارشی ذخیره کرده یا روی پیش‌فرض است؟ */
    isCustomized: boolean;
  };
  devices: Array<
    PushDevice & { isActive?: boolean; createdAt?: string }
  >;
  summary: {
    activeDevices: number;
    inactiveDevices: number;
    legacyTokens: number;
    canReceivePush: boolean;
  };
}

export const pushAdminApi = {
  /** جستجو با شمارهٔ موبایل / نام / ایمیل / شناسه. */
  async searchUsers(q: string): Promise<Result<PushUserSearchResult[]>> {
    try {
      const res = await apiClient.get('/admin/push/users', { params: { q } });
      return { success: true, data: res.data?.data ?? [] };
    } catch (error) {
      return fail(error, 'خطا در جستجوی کاربران');
    }
  },

  async getUserState(userId: string): Promise<Result<UserPushState>> {
    try {
      const res = await apiClient.get(`/admin/push/users/${userId}`);
      return { success: true, data: res.data?.data };
    } catch (error) {
      return fail(error, 'خطا در دریافت وضعیت پوش کاربر');
    }
  },

  /** تنظیمات اعلان کاربر → پیش‌فرض (همه فعال، بدون ساعت سکوت). */
  async resetPreferences(userId: string): Promise<Result<null>> {
    try {
      const res = await apiClient.post(`/admin/push/users/${userId}/reset-preferences`);
      return { success: true, message: res.data?.message };
    } catch (error) {
      return fail(error, 'خطا در بازنشانی تنظیمات');
    }
  },

  /** حذف همهٔ دستگاه‌ها — با اولین بازدید بعدی، هر دستگاه از نو ثبت می‌شود. */
  async clearDevices(userId: string): Promise<Result<{ removed: number }>> {
    try {
      const res = await apiClient.post(`/admin/push/users/${userId}/clear-devices`);
      return { success: true, data: res.data?.data, message: res.data?.message };
    } catch (error) {
      return fail(error, 'خطا در حذف دستگاه‌ها');
    }
  },

  async sendTest(userId: string): Promise<Result<null>> {
    try {
      const res = await apiClient.post(`/admin/push/users/${userId}/test`);
      return { success: true, message: res.data?.message };
    } catch (error) {
      return fail(error, 'خطا در ارسال اعلان تستی');
    }
  },
};

export default pushSettingsApi;
